// ─────────────────────────────────────────────────────────────────────────────
// App.js
// Root component — handles auth state, admin check, and routing.
//
// AUTH FLOW:
//   1. onAuthStateChange fires on mount with current session
//   2. checkAdmin queries profiles for is_superadmin flag
//   3. setLoading(false) is always called via .then() — never awaited inside
//      the listener to avoid Supabase auth lock deadlock
//
// ROUTES:
//   /login           — contractor login/signup
//   /dashboard       — contractor dashboard
//   /quotes          — quotes list
//   /quotes/:id      — quote detail
//   /invoices        — invoices list
//   /customers       — customers list
//   /payments        — contractor subscription & payment methods
//   /settings        — company settings
//   /admin/login     — admin login
//   /admin           — admin dashboard (requires is_superadmin)
//
// TO ADD A NEW ROUTE: Import the page component and add a <Route> below.
// ─────────────────────────────────────────────────────────────────────────────

import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { supabase } from './supabaseClient'
import Login from './pages/Login'
import Dashboard from './pages/Dashboard'
import Quotes from './pages/Quotes'
import Customers from './pages/Customers'
import Settings from './pages/Settings'
import Payments from './pages/Payments'
import Invoices from './pages/Invoices'
import InvoiceDetail from './pages/InvoiceDetail'
import QuoteDetail from './pages/QuoteDetail'
import AdminLogin from './pages/admin/AdminLogin'
import AdminDashboard from './pages/admin/AdminDashboard'

function App() {
  // ── State ──────────────────────────────────────────────────────────────────
  const [session, setSession] = useState(null)
  const [isSuperAdmin, setIsSuperAdmin] = useState(false)
  const [loading, setLoading] = useState(true)

  // ── Admin check ────────────────────────────────────────────────────────────
  // Queries the profiles table for is_superadmin flag.
  // Uses maybeSingle() to avoid hanging if no row is returned.
  const checkAdmin = async (userId) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('is_superadmin')
        .eq('id', userId)
        .maybeSingle()
      if (error) {
        console.warn('checkAdmin error:', error.message)
        setIsSuperAdmin(false)
        return
      }
      setIsSuperAdmin(data?.is_superadmin === true)
    } catch (e) {
      console.warn('checkAdmin exception:', e.message)
      setIsSuperAdmin(false)
    }
  }

  // ── Auth listener ──────────────────────────────────────────────────────────
  // IMPORTANT: Do not use await inside onAuthStateChange — it causes a
  // Supabase auth lock deadlock. Use .then() instead.
  useEffect(() => {
    let mounted = true

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        if (!mounted) return

        setSession(session)

        if (session?.user?.id) {
          checkAdmin(session.user.id).then(() => {
            if (mounted) setLoading(false)
          })
        } else {
          setIsSuperAdmin(false)
          setLoading(false)
        }
      }
    )

    return () => {
      mounted = false
      subscription.unsubscribe()
    }
  }, [])

  // ── Loading screen ─────────────────────────────────────────────────────────
  if (loading) return (
    <div style={{
      display: 'flex', justifyContent: 'center',
      alignItems: 'center', height: '100vh',
      fontFamily: 'Inter, sans-serif', color: '#2563EB', fontSize: '14px'
    }}>Loading...</div>
  )

  // ── Routes ─────────────────────────────────────────────────────────────────
  return (
    <Router>
      <Routes>
        {/* ── Auth ── */}
        <Route path="/login" element={!session ? <Login /> : <Navigate to="/dashboard" />} />

        {/* ── Contractor pages ── */}
        <Route path="/dashboard"  element={session ? <Dashboard  session={session} /> : <Navigate to="/login" />} />
        <Route path="/quotes"     element={session ? <Quotes     session={session} /> : <Navigate to="/login" />} />
        <Route path="/quotes/:id" element={session ? <QuoteDetail session={session} /> : <Navigate to="/login" />} />
        <Route path="/customers"  element={session ? <Customers  session={session} /> : <Navigate to="/login" />} />
        <Route path="/payments"   element={session ? <Payments   session={session} /> : <Navigate to="/login" />} />
        <Route path="/invoices"      element={session ? <Invoices      session={session} /> : <Navigate to="/login" />} />
        <Route path="/invoices/:id"  element={session ? <InvoiceDetail session={session} /> : <Navigate to="/login" />} />
        <Route path="/settings"   element={session ? <Settings   session={session} /> : <Navigate to="/login" />} />

        {/* ── Admin pages ── */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={
          session && isSuperAdmin
            ? <AdminDashboard session={session} />
            : <Navigate to="/admin/login" />
        } />

        {/* ── Fallback ── */}
        <Route path="*" element={<Navigate to="/login" />} />
      </Routes>
    </Router>
  )
}

export default App